import {
  SpaceBetween,
  ColumnLayout,
  FormField,
  Box,
  Select,
  RadioGroup,
  Input,
} from "@cloudscape-design/components-themed";
import { OptionDefinition } from "@cloudscape-design/components-themed/internal/components/option/interfaces";
import { isEmpty } from "lodash";
import { Dispatch, SetStateAction, useState, useEffect } from "react";
import {
  useGetFinancialInstitutionAllQuery,
  useGetFinancialInstitutionBranchQuery,
} from "../../../features/api";
import { BankAccountsUpdateRequest } from "../../../features/api-interfaces";
import {
  getFinancialInstitutionItems,
  getFinancialInstitutionBranchItems,
} from "../../../utils/common";
import {
  DivisionDictDescription,
  addBlankDescription,
  COMMON_LABELS,
  CODE_MASTER,
} from "../../default-constants";
import { IErrMsg, LABELS, CONSTRAINT_TEXT } from "../constants";
import {
  validRequired,
  validBankAccountNumber,
  validBankAccountHolderKana,
} from "../validation";

interface IBankAccountUpdate {
  editingRequest?: BankAccountsUpdateRequest;
  setEditingRequest?: Dispatch<SetStateAction<BankAccountsUpdateRequest>>;
  errMsg?: IErrMsg;
  setErrMsg?: Dispatch<SetStateAction<IErrMsg>>;
}

/**
 * 入金口座情報照会・更新 口座情報 更新
 */
export const BankAccountUpdate = (props: IBankAccountUpdate) => {
  const { editingRequest, setEditingRequest, errMsg, setErrMsg } = props;
  const [banks, setBanks] = useState([] as DivisionDictDescription[]);
  const [branches, setBranches] = useState([] as DivisionDictDescription[]);
  const { data: financialInstitution } = useGetFinancialInstitutionAllQuery();
  const { data: financialInstitutionBranch } =
    useGetFinancialInstitutionBranchQuery(editingRequest?.fnnInsMthStrCd!, {
      skip: isEmpty(editingRequest?.fnnInsMthStrCd),
    });

  useEffect(() => {
    setBanks(
      addBlankDescription(getFinancialInstitutionItems(financialInstitution))
    );
  }, [financialInstitution]);

  useEffect(() => {
    setBranches(
      addBlankDescription(
        getFinancialInstitutionBranchItems(financialInstitutionBranch)
      )
    );
  }, [financialInstitutionBranch]);

  /**
   * 金融機関の入力ハンドラ.
   * @param {OptionDefinition} selectedOption 入力値
   */
  const bankOnChange = (selectedOption: OptionDefinition) => {
    if (editingRequest && setEditingRequest && setErrMsg) {
      const inputValue = selectedOption.value ?? "";
      setEditingRequest({
        ...editingRequest,
        fnnInsMthStrCd: inputValue,
        fnnInsMthStrNm: selectedOption.label ?? "",
        fnnInsBrnCd: "",
        fnnInsBrnNm: "",
      });
      setErrMsg({ ...errMsg, fnnInsMthStrCd: validRequired(inputValue) });
    }
  };

  /**
   * 支店の入力ハンドラ.
   * @param {OptionDefinition} selectedOption 入力値
   */
  const branchOnChange = (selectedOption: OptionDefinition) => {
    if (editingRequest && setEditingRequest && setErrMsg) {
      const inputValue = selectedOption.value ?? "";
      setEditingRequest({
        ...editingRequest,
        fnnInsBrnCd: inputValue,
        fnnInsBrnNm: selectedOption.label ?? "",
      });
      setErrMsg({ ...errMsg, fnnInsBrnCd: validRequired(inputValue) });
    }
  };

  /**
   * 口座種別の入力ハンドラ.
   * @param {string} inputValue 入力値
   */
  const bankAccountTypeOnChange = (inputValue: string) => {
    if (editingRequest && setEditingRequest) {
      setEditingRequest({
        ...editingRequest,
        bankAccountType: Number(inputValue),
      });
    }
  };

  /**
   * 口座番号の入力ハンドラ.
   * @param {string} inputValue 入力値
   */
  const bankAccountNumberOnChange = (inputValue: string) => {
    if (editingRequest && setEditingRequest && setErrMsg) {
      setEditingRequest({
        ...editingRequest,
        bankAccountNumber: inputValue,
      });
      setErrMsg({
        ...errMsg,
        bankAccountNumber: validBankAccountNumber(inputValue),
      });
    }
  };

  /**
   * 口座名義カナの入力ハンドラ.
   * @param {string} inputValue 入力値
   */
  const bankAccountHolderKanaOnClick = (inputValue: string) => {
    if (editingRequest && setEditingRequest && setErrMsg) {
      setEditingRequest({
        ...editingRequest,
        bankAccountHolderKana: inputValue,
      });
      setErrMsg({
        ...errMsg,
        bankAccountHolderKana: validBankAccountHolderKana(inputValue),
      });
    }
  };

  return (
    <SpaceBetween direction={"vertical"} size={"l"}>
      <ColumnLayout columns={3}>
        <FormField
          label={
            <SpaceBetween direction={"horizontal"} size={"xs"}>
              <Box variant={"strong"}>{LABELS.BANK}</Box>
              <Box variant={"small"} color={"text-status-error"}>
                {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
              </Box>
            </SpaceBetween>
          }
          stretch={true}
          errorText={errMsg?.fnnInsMthStrCd}
        >
          <Select
            selectedOption={{
              value: editingRequest?.fnnInsMthStrCd ?? "",
              label: editingRequest?.fnnInsMthStrNm ?? "",
            }}
            options={banks}
            onChange={({ detail: { selectedOption } }) =>
              bankOnChange(selectedOption)
            }
            filteringType={"auto"}
          />
        </FormField>
      </ColumnLayout>
      <ColumnLayout columns={3}>
        <FormField
          label={
            <SpaceBetween direction={"horizontal"} size={"xs"}>
              <Box variant={"strong"}>{LABELS.BRANCH}</Box>
              <Box variant={"small"} color={"text-status-error"}>
                {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
              </Box>
            </SpaceBetween>
          }
          stretch={true}
          errorText={errMsg?.fnnInsBrnCd}
        >
          <Select
            selectedOption={{
              value: editingRequest?.fnnInsBrnCd ?? "",
              label: editingRequest?.fnnInsBrnNm ?? "",
            }}
            options={branches}
            filteringType={"auto"}
            onChange={({ detail: { selectedOption } }) =>
              branchOnChange(selectedOption)
            }
          />
        </FormField>
      </ColumnLayout>
      <ColumnLayout columns={3}>
        <SpaceBetween direction="vertical" size="l">
          <FormField
            label={
              <SpaceBetween direction={"horizontal"} size={"xs"}>
                <Box variant={"strong"}>{LABELS.BANK_ACCOUNT_TYPE}</Box>
                <Box variant={"small"} color={"text-status-error"}>
                  {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                </Box>
              </SpaceBetween>
            }
          >
            <RadioGroup
              items={CODE_MASTER["BANK_ACCOUNT_TYPE"]}
              onChange={({ detail: { value } }) =>
                bankAccountTypeOnChange(value)
              }
              value={String(editingRequest?.bankAccountType ?? "")}
            />
          </FormField>
          <FormField
            label={
              <SpaceBetween direction={"horizontal"} size={"xs"}>
                <Box variant={"strong"}>{LABELS.BANK_ACCOUNT_NUMBER}</Box>
                <Box variant={"small"} color={"text-status-error"}>
                  {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                </Box>
              </SpaceBetween>
            }
            constraintText={CONSTRAINT_TEXT.BANK_ACCOUNT_NUMBER}
            errorText={errMsg?.bankAccountNumber}
          >
            <Input
              value={editingRequest?.bankAccountNumber ?? ""}
              onChange={({ detail: { value } }) =>
                bankAccountNumberOnChange(value)
              }
            />
          </FormField>
          <FormField
            label={
              <SpaceBetween direction={"horizontal"} size={"xs"}>
                <Box variant={"strong"}>{LABELS.BANK_ACCOUNT_HOLDER_KANA}</Box>
                <Box variant={"small"} color={"text-status-error"}>
                  {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                </Box>
              </SpaceBetween>
            }
            constraintText={CONSTRAINT_TEXT.BANK_ACCOUNT_HOLDER_KANA}
            errorText={errMsg?.bankAccountHolderKana}
          >
            <Input
              value={editingRequest?.bankAccountHolderKana ?? ""}
              onChange={({ detail: { value } }) =>
                bankAccountHolderKanaOnClick(value)
              }
            />
          </FormField>
        </SpaceBetween>
      </ColumnLayout>
    </SpaceBetween>
  );
};
